import React, { PropsWithChildren } from "react";
import { useTranslation } from "i18n";
import {
  sendJobsfeedUsageHeapEventWithUserPrefix,
  JobsFeedUsageEvents,
} from "shared-services";
import { dataTestIds } from "data-testids";
import {
  HelpQuestionIcon as HelpIcon,
  SearchIcon,
  ThingsEventsTicketIcon as BenefitsIcon,
  UserOutlineIcon as UserIcon,
} from "assets";
import GenericAppShell, { GenericAppShellProps } from "../GenericAppShell";
import { ThemeContextValues } from "../NavThemeContext";
import LeftHandNavItem from "../LeftHandNavItem";
import QuickActionButton from "../QuickActionButton";
import NavItem from "../NavItem";
import { Pill } from "../../../../Atoms/Pill/Pill";
import { useGetAvatar } from "../../hooks/useGetAvatar";
import { performerViewPinPath } from "../../constants";

const FilmmakersAppShell = ({
  footerComponent,
  hideFooter,
  children,
}: PropsWithChildren<
  Pick<GenericAppShellProps, "hideFooter" | "footerComponent">
>) => {
  const { t } = useTranslation();
  const img = useGetAvatar();

  return (
    <GenericAppShell
      theme={ThemeContextValues.Filmmakers}
      hideFooter={hideFooter}
      footerComponent={footerComponent}
      avatar={img}
      leftHandNavItems={
        <>
          <LeftHandNavItem
            to="/jobs/matching-my-filters"
            icon={SearchIcon}
            onClick={() =>
              sendJobsfeedUsageHeapEventWithUserPrefix(
                JobsFeedUsageEvents.LhnMatchingMyFilter
              )
            }
          >
            {t("common:nav.links.jobsFeed.filters")}
          </LeftHandNavItem>
          <LeftHandNavItem
            to="/jobs/all-paid"
            isSubNav
            onClick={() =>
              sendJobsfeedUsageHeapEventWithUserPrefix(
                JobsFeedUsageEvents.LhnAllPaid
              )
            }
          >
            {t("common:nav.links.paidJobs")}
          </LeftHandNavItem>
          <LeftHandNavItem
            to="/jobs/all-opportunities"
            isSubNav
            onClick={() =>
              sendJobsfeedUsageHeapEventWithUserPrefix(
                JobsFeedUsageEvents.LhnAllOpportunities
              )
            }
          >
            {t("common:nav.links.allJobs")}
          </LeftHandNavItem>
          <LeftHandNavItem to="/jobs/self-tape-requests" isSubNav>
            {t("common:nav.links.mediaRequests")}
            <Pill className="bg-blue-sky-2 ml-1.5" theme="light" size="md">
              {t("common:nav.links.pill.beta")}
            </Pill>
          </LeftHandNavItem>
          <LeftHandNavItem
            to="/settings/jobs-feed"
            isSubNav
            onClick={() =>
              sendJobsfeedUsageHeapEventWithUserPrefix(
                JobsFeedUsageEvents.LhnJobsfeedSettings
              )
            }
          >
            {t("common:nav.links.jobsFeedSettings")}
          </LeftHandNavItem>

          <LeftHandNavItem
            to={"/profile/myviewpin"}
            nestedPaths={[performerViewPinPath]}
            icon={UserIcon}
          >
            {t("common:nav.links.usersProfile")}
          </LeftHandNavItem>
          <LeftHandNavItem to="/profile/linktoprofile" isSubNav>
            {t("common:nav.links.usersProfileLink")}
          </LeftHandNavItem>

          <LeftHandNavItem
            to="https://www.spotlight.com/the-small-print/what-we-offer-our-members/"
            icon={BenefitsIcon}
          >
            {t("common:nav.links.benefits")}
          </LeftHandNavItem>
          <LeftHandNavItem to="https://app.spotlight.com/contacts" isSubNav>
            {t("common:nav.links.contactsListings")}
          </LeftHandNavItem>
          <LeftHandNavItem
            to="https://www.spotlight.com/news-and-advice/"
            isSubNav
          >
            {t("common:nav.links.newsAndAdvice")}
          </LeftHandNavItem>
          <LeftHandNavItem
            to="https://www.spotlight.com/industry-resources/"
            isSubNav
          >
            {t("common:nav.links.industryResources")}
          </LeftHandNavItem>
        </>
      }
      dynamicHeaderContent={
        <QuickActionButton
          label={t("common:nav.links.jobsFeed")}
          linkUrl="/jobs/matching-my-filters"
        />
      }
      userMenuItems={
        <>
          <NavItem
            to="https://www.spotlight.com/help-and-faqs/"
            icon={HelpIcon}
            data-testid={
              dataTestIds.componentLibrary[
                "Organisms.NavigationHeader.MainNavigationItem.mainNavigationLink"
              ]
            }
          >
            {t("common:nav.links.helpAndTutorials")}
          </NavItem>
          <NavItem
            to="https://support.spotlight.com"
            data-testid={
              dataTestIds.componentLibrary[
                "Organisms.NavigationHeader.SubNavigationItem.subNavigationLink"
              ]
            }
            isSubNav
          >
            {t("common:nav.links.support")}
          </NavItem>
          <NavItem
            to="https://www.spotlight.com/rooms-and-studios/"
            data-testid={
              dataTestIds.componentLibrary[
                "Organisms.NavigationHeader.SubNavigationItem.subNavigationLink"
              ]
            }
            isSubNav
          >
            {t("common:nav.links.roomAndStudiosHire")}
          </NavItem>
        </>
      }
    >
      {children}
    </GenericAppShell>
  );
};

export default FilmmakersAppShell;
